/**
 * Contacts on a phone (SAM-1308): the table as cards, the search at full width, the pager under the list.
 * Presentational: Contacts.tsx owns the query and the page. The rows read the same fields the desktop table shows.
 */
import React from 'react';
import { PhoneListState, PhonePager } from '../components/phone/PhoneCards';
import { PhoneItemList, PhonePage, PhoneSearch, PhoneTitleRow, type PhoneItem } from '../components/phone/PhoneListParts';
import { useT } from '../i18n/admin';

export interface PhoneContact {
  id: string | number;
  title: string;
  guestId?: string | number | null;
  destination?: string | null;
  phone?: string | null;
  status?: string | null;
  from?: string | null;
}

interface Props {
  contacts: PhoneContact[];
  loading: boolean;
  query: string;
  onQuery: (q: string) => void;
  page: number;
  limit: number;
  total: number;
  onPage: (page: number) => void;
}

export const ContactsPhone: React.FC<Props> = ({ contacts, loading, query, onQuery, page, limit, total, onPage }) => {
  const { t } = useT();
  const items: PhoneItem[] = contacts.map((c) => ({
    key: String(c.id),
    title: c.title || t('phoneLists.contacts.untitled'),
    sub: [c.guestId != null && c.guestId !== '' ? t('phoneLists.contacts.guest', { id: c.guestId }) : null, c.destination].filter(Boolean).join(' · ') || undefined,
    extra: [c.phone, c.from ? t('phoneLists.contacts.from', { date: c.from }) : null].filter(Boolean).join(' · ') || undefined,
    status: c.status ? { label: c.status, tone: 'info' } : null,
  }));
  return (
    <PhonePage>
      <PhoneTitleRow title={t('phoneLists.contacts.title')} />
      <PhoneSearch placeholder={t('phoneLists.contacts.search')} value={query} onChange={onQuery} />
      <PhoneListState loading={loading} failed={false} empty={!loading && items.length === 0} emptyText={t('phoneLists.contacts.empty')} />
      {!loading && items.length > 0 && <PhoneItemList items={items} />}
      {!loading && <PhonePager page={page} limit={limit} total={total} onPage={onPage} />}
    </PhonePage>
  );
};
